/**
 * Ubicación por el puente NATIVO de Despia (porteado de Tactic, 2026-08-04).
 *
 * ¿Por qué no basta con `navigator.geolocation` dentro de la WebView?
 *
 *  · El permiso de la API web depende de que el contenedor nativo lo tenga
 *    cableado (en Android, `onGeolocationPermissionsShowPrompt`; en iOS, el
 *    puente de WKWebView). Cuando falla, el trabajador simplemente NO ve ningún
 *    diálogo y la app se queda sin ubicación sin decir por qué — que es justo
 *    lo que se reportó ("no me pide la ubicación al reportar plaga").
 *  · `location://simple` de Despia lo pide desde la capa nativa: una lectura
 *    simple solicita el permiso While-Using si no está concedido. El diálogo
 *    del sistema sale seguro.
 *
 * Se usa como PRIMERA opción en la app nativa y la API web queda de respaldo
 * (y como único detector de denegación: el puente de Despia no informa de
 * permisos denegados).
 *
 * ⚠️ TIMEOUT DURO OBLIGATORIO. `despia-native` sondea `window[key]` cada
 * 100 ms hasta 30 s cuando el runtime no contesta. Sin acotarlo, el reporte
 * dejaría al trabajador medio minuto mirando un spinner.
 *
 * Funciona sin cobertura: el GNSS no necesita red.
 */
import despia from "despia-native";
import { isDespiaNative } from "@/lib/isDespiaNative";

export interface NativeGeoFix {
  lat: number;
  lng: number;
  accuracyM: number;
}

/** Tope de espera del puente nativo. Por encima de esto, se da por fallido. */
const NATIVE_FIX_TIMEOUT_MS = 8000;

interface DespiaLocationSample {
  latitude?: unknown;
  longitude?: unknown;
  horizontalAccuracy?: unknown;
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

export function parseSample(raw: unknown): NativeGeoFix | null {
  if (!raw || typeof raw !== "object") return null;
  const s = raw as DespiaLocationSample;
  if (!isFiniteNumber(s.latitude) || !isFiniteNumber(s.longitude)) return null;
  return {
    lat: s.latitude,
    lng: s.longitude,
    // Si el runtime no da precisión, no se inventa una buena: se asume mala,
    // que en la regla del perímetro juega SIEMPRE a favor del trabajador.
    accuracyM: isFiniteNumber(s.horizontalAccuracy)
      ? s.horizontalAccuracy
      : Number.POSITIVE_INFINITY,
  };
}

/**
 * Lectura única por el puente nativo. Dispara el diálogo de permiso
 * "Al usar la app" si aún no estaba concedido.
 *
 * Devuelve `null` en web, si el puente no contesta a tiempo, o si no hay fix.
 * Nunca lanza: el llamante siempre puede caer a la API web.
 */
export async function getDespiaFix(
  timeoutMs: number = NATIVE_FIX_TIMEOUT_MS,
): Promise<NativeGeoFix | null> {
  if (!isDespiaNative()) return null;

  const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs));

  const read = (async (): Promise<NativeGeoFix | null> => {
    try {
      const data = (await despia("location://simple", ["locationSession"])) as
        | { locationSession?: unknown }
        | undefined;
      const session = data?.locationSession;
      const first = Array.isArray(session) ? session[0] : session;
      return parseSample(first);
    } catch {
      return null;
    }
  })();

  return Promise.race([read, timeout]);
}

/** ¿Merece la pena intentar el camino nativo? */
export function canUseNativeLocation(): boolean {
  return isDespiaNative();
}
